import { useCallback, useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { ViewportChangedEvent } from 'ag-grid-community'
import {
  CUSTOMS_ACTIVE_STATUSES,
  CUSTOMS_CHECK_STATUSES,
  CUSTOMS_DONE_STATUSES,
  type ChestnyZnak,
  type CustomsCheckStatus,
  type CustomsQueueQuery,
  type CustomsQueueResponse,
  type CustomsQueueRow,
  type CustomsQueueTab,
} from '@crm/shared'
import { getCustomsQueue, getCustomsQueueOptions } from '../../shared/api/customs.api'

/** Матрицы очереди нетарифки — те же значения, что принимает API (`CustomsQueueQuerySchema`). */
export const CUSTOMS_MATRIX_OPTIONS = ['Китай', 'Производители', 'Стоки'] as const
export type CustomsMatrixOption = (typeof CUSTOMS_MATRIX_OPTIONS)[number]

/** Статусы, которые можно выбрать внутри вкладки. */
export const TAB_STATUSES: Record<CustomsQueueTab, CustomsCheckStatus[]> = {
  active: CUSTOMS_ACTIVE_STATUSES,
  done: CUSTOMS_DONE_STATUSES,
  all: CUSTOMS_CHECK_STATUSES,
}

/** Размер страницы очереди: догружается при прокрутке. */
export const CUSTOMS_PAGE_SIZE = 200

export interface CustomsQueueFilters {
  tab: CustomsQueueTab
  status: CustomsCheckStatus | ''
  managerId: string
  matrix: CustomsMatrixOption | ''
  /** Только позиции с красным флагом «Внимание». */
  attention: boolean
  chestnyZnak: ChestnyZnak | ''
  /** По умолчанию позиции архивных заявок скрыты. */
  hideArchived: boolean
  /** Заявки, созданные с этой даты (ГГГГ-ММ-ДД). */
  requestFrom: string
}

export const DEFAULT_CUSTOMS_FILTERS: CustomsQueueFilters = {
  tab: 'active',
  status: '',
  managerId: '',
  matrix: '',
  attention: false,
  chestnyZnak: '',
  hideArchived: true,
  requestFrom: '',
}

/** Фильтры сайта → параметры `GET /customs/queue` (без offset — он у страницы). */
export function toCustomsQuery(f: CustomsQueueFilters, search: string): CustomsQueueQuery {
  return {
    tab: f.tab,
    ...(f.status ? { status: f.status } : {}),
    ...(search ? { search } : {}),
    ...(f.managerId ? { managerId: f.managerId } : {}),
    ...(f.matrix ? { matrix: f.matrix } : {}),
    ...(f.attention ? { attention: '1' as const } : {}),
    ...(f.chestnyZnak ? { chestnyZnak: f.chestnyZnak } : {}),
    hideArchived: f.hideArchived ? '1' : '0',
    ...(f.requestFrom ? { requestFrom: f.requestFrom } : {}),
    limit: CUSTOMS_PAGE_SIZE,
  }
}

/**
 * Строки всех загруженных страниц без дублей по позиции (первое вхождение).
 * Догрузка идёт по offset: если между страницами сверху появились новые позиции,
 * хвост прошлой страницы приходит ещё раз — без фильтра в гриде были бы
 * одинаковые id (AG Grid ругается, выделение путается).
 */
export function mergeQueuePages(pages: ReadonlyArray<CustomsQueueResponse>): CustomsQueueRow[] {
  const seen = new Set<string>()
  const rows: CustomsQueueRow[] = []
  for (const page of pages) {
    for (const row of page.items) {
      if (seen.has(row.itemId)) continue
      seen.add(row.itemId)
      rows.push(row)
    }
  }
  return rows
}

/**
 * Очередь нетарифки: фильтры, поиск с задержкой 300 мс, постраничная догрузка
 * (offset/limit на стороне базы) и справочник менеджеров для фильтра.
 * Без WebSocket: очередь перечитывается при открытии и возврате на вкладку
 * (`staleTime: 0`, `refetchOnWindowFocus: true`), решение 24.09.
 */
export function useCustomsQueue() {
  const [filters, setFilters] = useState<CustomsQueueFilters>(DEFAULT_CUSTOMS_FILTERS)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const updateFilters = useCallback((patch: Partial<CustomsQueueFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch }
      // Статус другой вкладки сбрасываем: «Ответ получен» во «Активных» не бывает.
      if (next.status && !TAB_STATUSES[next.tab].includes(next.status)) next.status = ''
      return next
    })
  }, [])

  const resetFilters = useCallback(() => {
    setFilters((prev) => ({ ...DEFAULT_CUSTOMS_FILTERS, tab: prev.tab }))
    setSearchInput('')
    setSearch('')
  }, [])

  const query = useMemo(() => toCustomsQuery(filters, search), [filters, search])

  const queue = useInfiniteQuery({
    queryKey: ['customs', 'queue', query],
    queryFn: ({ pageParam }) => getCustomsQueue({ ...query, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    // Смена фильтра не мигает загрузчиком: старые строки видны, пока идёт запрос.
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })

  const options = useQuery({
    queryKey: ['customs', 'options'],
    queryFn: getCustomsQueueOptions,
    staleTime: 60_000,
  })

  const rows: CustomsQueueRow[] = useMemo(
    () => mergeQueuePages(queue.data?.pages ?? []),
    [queue.data],
  )
  // Счётчики вкладок и «всего» сервер считает только на первой странице (offset 0).
  const firstPage = queue.data?.pages[0]
  const counts = firstPage?.counts ?? null
  const total = firstPage?.total ?? 0

  // Догрузка следующей страницы при прокрутке к концу (как в «Заказах»).
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = queue
  const onViewportChanged = useCallback(
    (e: ViewportChangedEvent) => {
      if (!hasNextPage || isFetchingNextPage) return
      const shown = e.api.getDisplayedRowCount()
      if (shown > 0 && e.lastRow >= shown - 20) void fetchNextPage()
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  const hasActiveFilters =
    !!search ||
    !!filters.status ||
    !!filters.managerId ||
    !!filters.matrix ||
    filters.attention ||
    !!filters.chestnyZnak ||
    !filters.hideArchived ||
    !!filters.requestFrom

  return {
    filters,
    updateFilters,
    resetFilters,
    hasActiveFilters,
    searchInput,
    setSearchInput,
    queue,
    rows,
    counts,
    total,
    managers: options.data?.managers ?? [],
    onViewportChanged,
  }
}
